"use client";

import { c as _c } from "react/compiler-runtime";
import { useInfiniteQuery, type QueryKey } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, type RefObject } from "react";
type InfiniteQueryAdapterOptions<TPage, TParams> = {
  queryKey: QueryKey;
  params: TParams;
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
  refetchOnMount?: boolean | "always";
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
  fetchPage: (params: TParams, page: number) => Promise<TPage>;
  getNextPage: (lastPage: TPage) => number | undefined;
};
export type InfiniteListScrollOptions = {
  containerRef?: RefObject<HTMLElement | null>;
  sentinelRef?: RefObject<HTMLDivElement | null>;
  sentinelRefs?: RefObject<HTMLElement | null>[];
};
type InfiniteListAdapterOptions<TPage, TParams, TItem> = InfiniteQueryAdapterOptions<TPage, TParams> & {
  selectItems: (page: TPage) => TItem[];
  scroll?: InfiniteListScrollOptions;
};

/**
 * Shared page-based adapter. Domain hooks own routes, response mapping, and
 * query keys; this hook owns TanStack's cursor/page lifecycle.
 */
export function useInfiniteQueryAdapter(t0) {
  const $ = _c(13);
  const {
    queryKey,
    params,
    enabled: t1,
    staleTime,
    gcTime,
    refetchOnMount,
    refetchOnWindowFocus,
    refetchOnReconnect,
    fetchPage,
    getNextPage
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== fetchPage || $[1] !== params) {
    t2 = t3 => {
      const {
        pageParam
      } = t3;
      return fetchPage(params, pageParam);
    };
    $[0] = fetchPage;
    $[1] = params;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== enabled || $[4] !== gcTime || $[5] !== getNextPage || $[6] !== queryKey || $[7] !== refetchOnMount || $[8] !== refetchOnReconnect || $[9] !== refetchOnWindowFocus || $[10] !== staleTime || $[11] !== t2) {
    t3 = {
      queryKey,
      queryFn: t2,
      initialPageParam: 0,
      getNextPageParam: getNextPage,
      enabled,
      staleTime,
      gcTime,
      refetchOnMount,
      refetchOnWindowFocus,
      refetchOnReconnect
    };
    $[3] = enabled;
    $[4] = gcTime;
    $[5] = getNextPage;
    $[6] = queryKey;
    $[7] = refetchOnMount;
    $[8] = refetchOnReconnect;
    $[9] = refetchOnWindowFocus;
    $[10] = staleTime;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return useInfiniteQuery(t3);
}

/**
 * Shared infinite-list state. Domain hooks provide the page item selector and
 * retain ownership of request details and feature-specific return values.
 */
export function useInfiniteListAdapter(t0) {
  const $ = _c(29);
  let queryOptions;
  let scroll;
  let selectItems;
  let t1;
  if ($[0] !== t0) {
    ({
      selectItems,
      scroll,
      enabled: t1,
      ...queryOptions
    } = t0);
    $[0] = t0;
    $[1] = queryOptions;
    $[2] = scroll;
    $[3] = selectItems;
    $[4] = t1;
  } else {
    queryOptions = $[1];
    scroll = $[2];
    selectItems = $[3];
    t1 = $[4];
  }
  const enabled = t1 === undefined ? true : t1;
  let t2;
  if ($[5] !== enabled || $[6] !== queryOptions) {
    t2 = {
      ...queryOptions,
      enabled
    };
    $[5] = enabled;
    $[6] = queryOptions;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const query = useInfiniteQueryAdapter(t2);
  let t3;
  if ($[8] !== query.data?.pages || $[9] !== selectItems) {
    t3 = query.data?.pages.flatMap(selectItems) ?? [];
    $[8] = query.data?.pages;
    $[9] = selectItems;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const items = t3;
  const hasMore = query.hasNextPage ?? false;
  const {
    fetchNextPage,
    isFetchingNextPage
  } = query;
  let t4;
  if ($[11] !== fetchNextPage || $[12] !== hasMore || $[13] !== isFetchingNextPage) {
    t4 = () => {
      if (hasMore && !isFetchingNextPage) {
        fetchNextPage();
      }
    };
    $[11] = fetchNextPage;
    $[12] = hasMore;
    $[13] = isFetchingNextPage;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const loadMore = t4;
  const t5 = enabled && Boolean(scroll);
  const t6 = scroll?.containerRef;
  const t7 = scroll?.sentinelRef;
  const t8 = scroll?.sentinelRefs;
  let t9;
  if ($[15] !== hasMore || $[16] !== isFetchingNextPage || $[17] !== loadMore || $[18] !== t5 || $[19] !== t6 || $[20] !== t7 || $[21] !== t8) {
    t9 = {
      enabled: t5,
      hasNextPage: hasMore,
      isFetchingNextPage,
      fetchNextPage: loadMore,
      scrollContainerRef: t6,
      sentinelRef: t7,
      sentinelRefs: t8
    };
    $[15] = hasMore;
    $[16] = isFetchingNextPage;
    $[17] = loadMore;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const loadMoreRef = useInfiniteScrollTrigger(t9);
  let t10;
  if ($[23] !== hasMore || $[24] !== items || $[25] !== loadMore || $[26] !== loadMoreRef || $[27] !== query) {
    t10 = {
      ...query,
      items,
      hasMore,
      loadMore,
      loadMoreRef
    };
    $[23] = hasMore;
    $[24] = items;
    $[25] = loadMore;
    $[26] = loadMoreRef;
    $[27] = query;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
}
type InfiniteScrollTriggerOptions = {
  enabled?: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  scrollContainerRef?: RefObject<HTMLElement | null>;
  sentinelRef?: RefObject<HTMLDivElement | null>;
  sentinelRefs?: RefObject<HTMLElement | null>[];
};
function getScrollParent(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement;
  while (parent) {
    const {
      overflowY
    } = globalThis.getComputedStyle(parent);
    if (["auto", "scroll", "overlay"].includes(overflowY)) return parent;
    parent = parent.parentElement;
  }
  return null;
}

/**
 * Reusable sentinel observer. All data/cache work stays in domain hooks.
 */
export function useInfiniteScrollTrigger(t0) {
  const $ = _c(9);
  const {
    enabled: t1,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    scrollContainerRef,
    sentinelRef: suppliedSentinelRef,
    sentinelRefs
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const internalSentinelRef = useRef(null);
  const loadMoreRef = suppliedSentinelRef ?? internalSentinelRef;
  let t2;
  let t3;
  if ($[0] !== enabled || $[1] !== fetchNextPage || $[2] !== hasNextPage || $[3] !== isFetchingNextPage || $[4] !== loadMoreRef || $[5] !== scrollContainerRef || $[6] !== sentinelRefs) {
    t2 = () => {
      if (!enabled) {
        return;
      }
      let observer;
      let rafId = 0;
      const observeVisibleSentinel = () => {
        observer?.disconnect();
        const sentinel = sentinelRefs?.map(_temp).find(_temp2) ?? loadMoreRef.current;
        if (!sentinel) {
          return;
        }
        observer = new IntersectionObserver(t4 => {
          const [entry] = t4;
          if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        }, {
          root: scrollContainerRef?.current ?? getScrollParent(sentinel),
          rootMargin: "100px",
          threshold: 0
        });
        observer.observe(sentinel);
      };
      observeVisibleSentinel();
      rafId = requestAnimationFrame(() => {
        observeVisibleSentinel();
      });
      window.addEventListener("resize", observeVisibleSentinel);
      return () => {
        cancelAnimationFrame(rafId);
        window.removeEventListener("resize", observeVisibleSentinel);
        observer?.disconnect();
      };
    };
    t3 = [enabled, fetchNextPage, hasNextPage, isFetchingNextPage, loadMoreRef, scrollContainerRef, sentinelRefs];
    $[0] = enabled;
    $[1] = fetchNextPage;
    $[2] = hasNextPage;
    $[3] = isFetchingNextPage;
    $[4] = loadMoreRef;
    $[5] = scrollContainerRef;
    $[6] = sentinelRefs;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  return loadMoreRef;
}
function _temp2(element) {
  return element?.offsetParent !== null;
}
function _temp(ref) {
  return ref.current;
}